import React from 'react';
import { LucideIcon } from 'lucide-react';

interface StatsCardProps {
  label: string;
  value: string | number;
  icon?: LucideIcon;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  variant?: 'default' | 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'violet';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  // Rende la scheda interattiva (es. per aprire il dettaglio del dato mostrato)
  onClick?: () => void;
}

const StatsCard: React.FC<StatsCardProps> = ({
  label,
  value,
  icon: Icon,
  trend,
  variant = 'default',
  size = 'md',
  className = '',
  onClick
}) => {
  const variantStyles = {
    default: {
      bg: 'bg-white dark:bg-gray-800',
      iconBg: 'bg-slate-100 dark:bg-gray-700',
      iconColor: 'text-slate-600 dark:text-slate-300',
      valueColor: 'text-slate-900 dark:text-slate-100',
      labelColor: 'text-slate-500 dark:text-slate-400'
    },
    brand: {
      bg: 'bg-gradient-to-br from-[#fff7a8]/30 to-[#fdf751]/30 dark:from-[#fff7a8]/20 dark:to-[#fdf751]/20',
      iconBg: 'bg-gradient-to-br from-[#fff7a8] to-[#fdf751]',
      iconColor: 'text-slate-800',
      valueColor: 'text-slate-900 dark:text-slate-100',
      labelColor: 'text-slate-600 dark:text-slate-300'
    },
    success: {
      bg: 'bg-green-50 dark:bg-green-900/30',
      iconBg: 'bg-green-100 dark:bg-green-800/50',
      iconColor: 'text-green-600 dark:text-green-400',
      valueColor: 'text-green-700 dark:text-green-400',
      labelColor: 'text-green-600 dark:text-green-400'
    },
    warning: {
      bg: 'bg-amber-50 dark:bg-amber-900/30',
      iconBg: 'bg-amber-100 dark:bg-amber-800/50',
      iconColor: 'text-amber-600 dark:text-amber-400',
      valueColor: 'text-amber-700 dark:text-amber-400',
      labelColor: 'text-amber-600 dark:text-amber-400'
    },
    danger: {
      bg: 'bg-red-50 dark:bg-red-900/30',
      iconBg: 'bg-red-100 dark:bg-red-800/50',
      iconColor: 'text-red-600 dark:text-red-400',
      valueColor: 'text-red-700 dark:text-red-400',
      labelColor: 'text-red-600 dark:text-red-400'
    },
    info: {
      bg: 'bg-blue-50 dark:bg-blue-900/30',
      iconBg: 'bg-blue-100 dark:bg-blue-800/50',
      iconColor: 'text-blue-600 dark:text-blue-400',
      valueColor: 'text-blue-700 dark:text-blue-400',
      labelColor: 'text-blue-600 dark:text-blue-400'
    },
    violet: {
      bg: 'bg-violet-50 dark:bg-violet-900/30',
      iconBg: 'bg-violet-100 dark:bg-violet-800/50',
      iconColor: 'text-violet-600 dark:text-violet-400',
      valueColor: 'text-violet-700 dark:text-violet-400',
      labelColor: 'text-violet-600 dark:text-violet-400'
    }
  };

  // Responsive size styles
  const sizeStyles = {
    sm: {
      padding: 'p-3 sm:p-4',
      iconSize: 'w-8 h-8 sm:w-8 sm:h-8',
      iconInner: 'w-4 h-4',
      valueSize: 'text-lg sm:text-xl',
      labelSize: 'text-xs',
      container: 'flex-col sm:flex-row'
    },
    md: {
      padding: 'p-3 sm:p-4 md:p-5',
      iconSize: 'w-9 h-9 sm:w-10 sm:h-10',
      iconInner: 'w-4 h-4 sm:w-5 sm:h-5',
      valueSize: 'text-xl sm:text-2xl',
      labelSize: 'text-xs sm:text-sm',
      container: 'flex-col sm:flex-row'
    },
    lg: {
      padding: 'p-4 sm:p-5 md:p-6',
      iconSize: 'w-10 h-10 sm:w-12 sm:h-12',
      iconInner: 'w-5 h-5 sm:w-6 sm:h-6',
      valueSize: 'text-2xl sm:text-3xl',
      labelSize: 'text-xs sm:text-sm',
      container: 'flex-col sm:flex-row'
    }
  };

  const styles = variantStyles[variant];
  const sizes = sizeStyles[size];

  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={`
        ${styles.bg} ${sizes.padding}
        rounded-xl sm:rounded-2xl
        border border-slate-200/60 dark:border-gray-700/60
        transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5
        w-full
        ${onClick ? 'cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-[#fdf751]/40' : ''}
        ${className}
      `}
      style={{
        boxShadow: 'none'
      }}
    >
      <div className={`flex items-start justify-between gap-3 ${sizes.container}`}>
        {/* Icon on top for mobile, right side for larger screens */}
        {Icon && (
          <div className={`${sizes.iconSize} ${styles.iconBg} rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 order-first sm:order-last sm:ml-3`}>
            <Icon className={`${sizes.iconInner} ${styles.iconColor}`} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className={`${sizes.labelSize} font-medium ${styles.labelColor} truncate`}>
            {label}
          </p>
          <div className="flex flex-wrap items-baseline gap-1 sm:gap-2 mt-0.5 sm:mt-1">
            <p
              className={`${sizes.valueSize} font-bold ${styles.valueColor}`}
              style={{ letterSpacing: '-0.02em' }}
            >
              {value}
            </p>
            {trend && (
              <span className={`text-[10px] sm:text-xs font-medium ${trend.isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {trend.isPositive ? '+' : ''}{trend.value}%
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatsCard;
